import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import HeartIcon from "../../assets/icons/HeartIcon";
import CartIcon from "../../assets/icons/CartIcon";
import favouriteService from "../../services/favourite.service";
import "./ProductCard.css";

const FALLBACK_IMAGE =
  "https://placehold.co/300x300/f0ede6/b0b5a8?text=No+Image";

const ProductCard = ({
  product,
  onNavigate,
  productId,
  onAddToCart,
  cartItems = [],
  onQuantityChange,
  onRemoveItem,
  favouriteItems = [],
}) => {
  const navigate = useNavigate();
  const [isAdding, setIsAdding] = useState(false);
  const [swingAnim, setSwingAnim] = useState(false);
  const [isFavourite, setIsFavourite] = useState(false);

  const currentId = product.product_id || product.id;
  const imageUrl = product.images?.[0] || FALLBACK_IMAGE;

  // NEW: Check if the item is out of stock.
  // IMPORTANT: Change 'product.stock' to match your database field (e.g., product.quantity)
  const isOutOfStock = product.stock_level <= 0;

  const cartItem = cartItems.find(
    (item) => item.productId === currentId || item.id === currentId,
  );
  const quantity = cartItem ? cartItem.quantity : 0;

  // Calculate discount
  const hasDiscount = product.old_price > product.price_display;
  const discountPct = hasDiscount
    ? Math.round(
        ((product.old_price - product.price_display) / product.old_price) * 100,
      )
    : 0;

  useEffect(() => {
    const isFav = favouriteItems.some(
      (item) => item.id === currentId || item.product_id === currentId,
    );
    setIsFavourite(isFav);
  }, [favouriteItems, currentId]);

  const handleNavigate = () => {
    if (typeof onNavigate === "function") {
      onNavigate("product", productId);
    } else {
      navigate(`/product/${productId}`);
    }
  };

  const handleAddClick = (e) => {
    e.stopPropagation();
    if (isOutOfStock) return; // NEW: Prevent adding if out of stock just in case
    setIsAdding(true);
    setTimeout(async () => {
      await onAddToCart(product);
      setIsAdding(false);
      setSwingAnim(true);
      setTimeout(() => setSwingAnim(false), 500);
    }, 800);
  };

  const handleIncrease = (e) => {
    e.stopPropagation();
    if (cartItem && onQuantityChange && !isOutOfStock) {
      // NEW: added stock check
      setIsAdding(true);
      setTimeout(async () => {
        await onQuantityChange(cartItem.cartItemId, quantity + 1);
        setIsAdding(false);
        setSwingAnim(true);
        setTimeout(() => setSwingAnim(false), 500);
      }, 500);
    }
  };

  const handleDecrease = (e) => {
    e.stopPropagation();
    if (cartItem) {
      setIsAdding(true);
      setTimeout(async () => {
        if (quantity === 1 && onRemoveItem) {
          await onRemoveItem(cartItem.cartItemId);
        } else if (onQuantityChange) {
          await onQuantityChange(cartItem.cartItemId, quantity - 1);
        }
        setIsAdding(false);
      }, 500);
    }
  };

  const toggleFavourite = async (e) => {
    e.stopPropagation();
    // ... [Your existing toggleFavourite logic remains exactly the same]
    const previousState = isFavourite;
    setIsFavourite(!previousState);
    try {
      if (previousState) {
        const res = await favouriteService.removeFavourite(currentId);
        if (res?.error === "notLoggedIn") {
          setIsFavourite(previousState);
          navigate("/auth");
          return;
        }
      } else {
        const res = await favouriteService.addFavourite(currentId);
        if (res?.error === "notLoggedIn") {
          setIsFavourite(previousState);
          navigate("/auth");
          return;
        }
      }
      window.dispatchEvent(new Event("favouritesChanged"));
    } catch (error) {
      console.error("Failed to update favourite status:", error);
      setIsFavourite(previousState);
    }
  };

  return (
    // NEW: Add a class to dim the whole card if out of stock
    <div
      className={`pc-card ${swingAnim ? "pc-swing" : ""} ${isOutOfStock ? "pc-out-of-stock" : ""}`}
    >
      {/* Loading overlay */}
      {isAdding && (
        <div className="pc-overlay">
          <div className="pc-spinner" />
        </div>
      )}

      {/* Discount badge — top-left. NEW: Hide discount if out of stock */}
      {hasDiscount && !isOutOfStock && (
        <div className="pc-discount-badge">{discountPct}% OFF</div>
      )}

      {/* Clickable product area */}
      <div
        className="pc-link"
        onClick={handleNavigate}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleNavigate()}
      >
        {/* Image */}
        <div className="pc-image-wrap">
          <img
            src={imageUrl}
            alt={product.name}
            loading="lazy"
            className="pc-image"
            onError={(e) => {
              e.target.src = FALLBACK_IMAGE;
            }}
          />

          {/* NEW: Display a bold Out of Stock overlay on the image */}
          {isOutOfStock && <div className="pc-oos-overlay">OUT OF STOCK</div>}

          {/* Favourite button */}
          <button
            className={`pc-fav-btn ${isFavourite ? "is-active" : ""}`}
            aria-label={
              isFavourite ? "Remove from Favourites" : "Add to Favourites"
            }
            onClick={toggleFavourite}
          >
            <HeartIcon />
          </button>
        </div>

        {/* Info */}
        <div className="pc-info">
          {/* Category chip if available */}
          {product.category_name && (
            <span className="pc-category">{product.category_name}</span>
          )}

          <h3 className="pc-name">{product.name}</h3>

          {product.price_display && (
            <div className="pc-pricing">
              {hasDiscount && (
                <div className="pc-old-row">
                  <span className="pc-old-price">₹{product.old_price}</span>
                  <span className="pc-save-text">
                    Save ₹{product.old_price - product.price_display}
                  </span>
                </div>
              )}
              <div className="pc-current-price">₹{product.price_display}</div>
            </div>
          )}
        </div>
      </div>

      {/* CTA — NEW: Handle Out of Stock state first */}
      {isOutOfStock ? (
        <button className="pc-add-btn pc-oos-btn" disabled>
          Out of Stock !
        </button>
      ) : quantity > 0 ? (
        <div className="pc-qty-bar" onClick={(e) => e.stopPropagation()}>
          <button
            className="pc-qty-btn"
            onClick={handleDecrease}
            aria-label="Decrease"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M3 7h8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <span className="pc-qty-num">{quantity}</span>
          <button
            className="pc-qty-btn"
            onClick={handleIncrease}
            aria-label="Increase"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path
                d="M7 3v8M3 7h8"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      ) : (
        <button className="pc-add-btn" onClick={handleAddClick}>
          <span className="pc-btn-icon">
            <CartIcon />
          </span>
          Add to Cart
        </button>
      )}
    </div>
  );
};

export default ProductCard;
